import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';

import env from 'Env';
import Breadcrumbs from 'Components/Utility/SEO/Breadcrumbs';
import BundleBreadcrumbs from 'Components/Utility/SEO/BundleBreadcrumbs';
import GiftCardStrings from 'Helpers/SEO/GiftCardStrings';

const PRODUCT_DLC_PAGE_THRESHOLD = env.productDlcPageThreshold;

const messages = defineMessages({
  games: {
    id: 'breadcrumbs.genre-games',
    defaultMessage: '{genre} Games',
  },
  allGames: {
    id: 'breadcrumbs.all-games',
    defaultMessage: 'PC Games',
  },
  reviews: {
    id: 'breadcrumbs.reviews',
    defaultMessage: 'Reviews',
  },
  dlc: {
    id: 'breadcrumbs.dlc',
    defaultMessage: 'All DLC',
  },
  franchise: {
    id: 'breadcrumbs.franchise',
    defaultMessage: '{franchiseName} Franchise',
  },
});

const ProductBreadcrumbs = ({ product, isReviews, isDLC, intl: { formatMessage } }) => {
  if (!product) {
    return false;
  }

  if (product.type === 'bundle') {
    return <BundleBreadcrumbs isReviews={isReviews} product={product} type="bundle" />;
  }

  const crumbs = [];

  if (product.display_type === 'gift-card') {
    crumbs.push({
      label: formatMessage(GiftCardStrings.all),
      link: '/gift-cards',
    });

    if (product.drm.eaplay) {
      crumbs.push({
        label: formatMessage(GiftCardStrings.eaplayTitle),
        link: '/gift-cards/ea-play',
      });
    }

    if (product.drm.xbox) {
      crumbs.push({
        label: formatMessage(GiftCardStrings.xboxTitle),
        link: '/gift-cards/xbox',
      });
    }

    if (product.drm.switch) {
      crumbs.push({
        label: formatMessage(GiftCardStrings.switchTitle),
        link: '/gift-cards/nintendo',
      });
    }

    if (product.drm.playstation) {
      crumbs.push({
        label: formatMessage(GiftCardStrings.playstationTitle),
        link: '/gift-cards/playstation',
      });
    }

    if (product.drm.roblox) {
      crumbs.push({
        label: formatMessage(GiftCardStrings.robloxTitle),
        link: '/gift-cards/roblox',
      });
    }
  } else {
    crumbs.push({
      label: formatMessage(messages.allGames),
      link: '/all-games',
    });
  }

  const genres = get(product, 'genres', []);
  const linkableGenres = genres.filter(g => g.link);

  if (linkableGenres.length) {
    const genre = linkableGenres[0];
    crumbs.push({
      label: `${formatMessage(messages.games, { genre: capitalize(genre.name) })}`,
      link: `/categories/${genre.slug}`,
    });
  }

  const combos = get(product, 'combo', []);

  const linkableAndCombos = combos.filter(g =>
    g.link && !Object.keys(g.combo).every(k => g.combo[k].or && g.combo[k].or.length),
  );

  if (linkableAndCombos.length) {
    const combo = linkableAndCombos[0];
    crumbs.push({
      label: `${formatMessage(messages.games, { genre: capitalize(combo.name) })}`,
      link: `/categories/${combo.slug}`,
    });
  }

  let currentAdded = false;

  if (product.editions && product.editions.length > 1 && product.edition_name && product.display_type !== 'gift-card') {
    const baseEdition = product.editions.find(e => !e.parent_slug);

    if (baseEdition && baseEdition.slug !== product.slug) {
      crumbs.push({
        label: baseEdition.name,
        link: `/${baseEdition.type}/${baseEdition.slug}`,
      });

      crumbs.push({
        label: product.edition_name,
        link: `/${product.type}/${product.slug}`,
      });

      currentAdded = true;
    }
  } else if (product.base_game && product.display_type !== 'gift-card') {
    crumbs.push({
      label: product.base_game.name,
      link: `/${product.base_game.type}/${product.base_game.slug}`,
    });

    const dlc = product.relatedHits && product.relatedHits.filter(h => h.type === 'dlc');

    const dlcCountThreshold = product.type === 'dlc' ? PRODUCT_DLC_PAGE_THRESHOLD - 1 : PRODUCT_DLC_PAGE_THRESHOLD;

    if ((dlc || []).length >= dlcCountThreshold) {
      crumbs.push({
        label: formatMessage(messages.dlc),
        link: `/${product.base_game.type}/${product.base_game.slug}/dlc`,
      });
    }

    crumbs.push({
      label: product.name.replace(product.base_game.name, '').replace(/^(\W)+/, ''),
      link: `/${product.type}/${product.slug}`,
    });

    currentAdded = true;
  }

  if (!currentAdded) {
    crumbs.push({
      label: product.name,
      link: `/${product.type}/${product.slug}`,
    });
  }

  if (isReviews) {
    crumbs.push({
      label: formatMessage(messages.reviews),
      link: `/${product.type}/${product.slug}/reviews`,
    });
  }

  if (isDLC) {
    crumbs.push({
      label: formatMessage(messages.dlc),
      link: `/${product.type}/${product.slug}/dlc`,
    });
  }

  const labels = crumbs.map(c => c.label);
  const duplicates = labels.filter((item, index) => index !== labels.indexOf(item));

  if (duplicates.length) {
    duplicates.forEach((duplicate) => {
      const matchedFranchise = crumbs.findIndex(c => c.label === duplicate && c.link.includes('franchises'));
      if (matchedFranchise > -1) {
        crumbs[matchedFranchise].label = formatMessage(messages.franchise, {
          franchiseName: crumbs[matchedFranchise].label,
        });
      }
    });
  }

  return <Breadcrumbs crumbs={crumbs} appearsOn="product" />;
};

export default injectIntl(ProductBreadcrumbs);

ProductBreadcrumbs.propTypes = {
  product: PropTypes.shape({}).isRequired,
  isReviews: PropTypes.bool,
  isDLC: PropTypes.bool,
};

ProductBreadcrumbs.defaultProps = {
  isReviews: false,
  isDLC: false,
};
